import React, { useRef } from 'react';
import { Select } from './kit';

/**
 * A period: a preset window ('7d', '30d', …, 'all') or a custom range of two
 * days, both included (owner, 2026-10-01: "there is no option for custom
 * date range" on the Analytics tab). Days are UTC calendar days — the same
 * the server cuts on (admin_v3.data.period_bounds) — unless the caller passes
 * its own `today` (Recrawl → Changes cuts on India days, as its server does).
 *
 * Switching to Custom fills both dates from the preset it came from, so the
 * range is never silently empty; an invalid range is shown inline and the
 * last valid one stays in force (usePeriodParams).
 */
export type Period = { preset: string; from: string; to: string };

const utcToday = () => new Date().toISOString().slice(0, 10);
const daysBefore = (iso: string, n: number) => {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() - n);
  return d.toISOString().slice(0, 10);
};
const dayLabel = (iso: string, withYear: boolean) => {
  const d = new Date(`${iso}T00:00:00Z`);
  return isNaN(d.getTime()) ? iso
    : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', ...(withYear ? { year: 'numeric' } : {}), timeZone: 'UTC' });
};

/** A preset as explicit days, for endpoints that take only from / to:
 *  {} for all time, else the last N days up to today (UTC unless `today` says otherwise). */
export const presetRange = (preset: string, presetDays: Record<string, number | null>,
                            today: () => string = utcToday): Record<string, string> => {
  const days = presetDays[preset];
  if (!days) return {};
  const to = today();
  return { from: daysBefore(to, days - 1), to };
};

/** What is wrong with a custom range ('' when nothing). */
export const periodError = (p: Period): string => {
  if (p.preset !== 'custom') return '';
  if (!p.from && !p.to) return 'Pick a start or an end date.';
  if (p.from && p.to && p.from > p.to) return 'The start date is after the end date.';
  return '';
};

/** "1 Sep – 30 Sep 2026", "from 1 Sep 2026", or the preset's label. */
export const periodText = (p: Period, labels: Record<string, string>): string => {
  if (p.preset !== 'custom') return labels[p.preset] || p.preset;
  if (p.from && p.to) {
    const sameYear = p.from.slice(0, 4) === p.to.slice(0, 4);
    return p.from === p.to ? dayLabel(p.from, true) : `${dayLabel(p.from, !sameYear)} – ${dayLabel(p.to, true)}`;
  }
  return p.from ? `from ${dayLabel(p.from, true)}` : p.to ? `up to ${dayLabel(p.to, true)}` : 'Custom range';
};

/** The query params for a period: {window} or {window: 'custom', from, to}.
 *  While the picker holds an invalid range, the last valid params stay. */
export function usePeriodParams(p: Period, key = 'window'): { params: Record<string, string>; error: string } {
  const last = useRef<Record<string, string>>({ [key]: p.preset === 'custom' ? 'all' : p.preset });
  const error = periodError(p);
  if (!error) {
    const params: Record<string, string> = { [key]: p.preset };
    if (p.preset === 'custom') {
      if (p.from) params.from = p.from;
      if (p.to) params.to = p.to;
    }
    last.current = params;
  }
  return { params: last.current, error };
}

const dateCls = 'h-9 px-2 rounded-md border-[0.5px] border-primary-300 bg-white text-sm';

export const PeriodPicker: React.FC<{
  value: Period; onChange: (p: Period) => void;
  /** Presets in order, 'custom' among them. */
  options: string[]; labels: Record<string, string>;
  /** How many days each preset covers (null = all time), to prefill Custom. */
  presetDays?: Record<string, number | null>;
  /** What the dates mean, shown under them. */
  hint?: string;
  /** The calendar day that is "today" for this endpoint ('YYYY-MM-DD'); UTC by default. */
  today?: () => string;
}> = ({ value, onChange, options, labels, presetDays = {}, hint, today: todayFn = utcToday }) => {
  const pick = (preset: string) => {
    if (preset !== 'custom') { onChange({ preset, from: '', to: '' }); return; }
    if (value.preset === 'custom') return;
    const to = todayFn();
    const days = presetDays[value.preset];
    onChange({ preset, from: days ? daysBefore(to, days - 1) : daysBefore(to, 29), to });
  };
  const err = periodError(value);
  const today = todayFn();
  return (
    <div className="flex flex-col items-end gap-1">
      <div className="flex flex-wrap items-center justify-end gap-2">
        <Select value={value.preset} onChange={pick} options={options} labels={labels} />
        {value.preset === 'custom' && (
          <>
            <input type="date" aria-label="From" value={value.from} max={value.to || today}
              onChange={e => onChange({ ...value, from: e.target.value })} className={dateCls} />
            <span className="text-xs text-grayscale-1100">to</span>
            <input type="date" aria-label="To" value={value.to} min={value.from || undefined} max={today}
              onChange={e => onChange({ ...value, to: e.target.value })} className={dateCls} />
          </>
        )}
      </div>
      {value.preset === 'custom' && (
        <span className={`text-[11px] ${err ? 'text-red-700' : 'text-grayscale-1100'}`}>
          {err || hint || 'Both days included (UTC). Leave one blank for open-ended.'}
        </span>
      )}
    </div>
  );
};
